'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

interface QuickOption {
  icon: string;
  titleAr: string;
  titleEn: string;
  descriptionAr: string;
  descriptionEn: string;
  actionAr: string;
  actionEn: string;
  link: string;
  color: string;
}

const QuickContactOptions = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const quickOptions: QuickOption[] = [
    {
      icon: 'PhoneIcon',
      titleAr: 'اتصل بنا مباشرة',
      titleEn: 'Call Us Directly',
      descriptionAr: 'تحدث مع فريقنا الآن',
      descriptionEn: 'Talk to our team now',
      actionAr: 'اتصال',
      actionEn: 'Call',
      link: 'tel:+966590041741',
      color: 'bg-success/10 text-success border-success/20',
    },
    {
      icon: 'EnvelopeIcon',
      titleAr: 'راسلنا عبر البريد',
      titleEn: 'Email Us',
      descriptionAr: 'سنرد خلال 24 ساعة',
      descriptionEn: 'We will reply within 24 hours',
      actionAr: 'إرسال',
      actionEn: 'Send',
      link: 'mailto:info@ibdaa-elherfa.com',
      color: 'bg-primary/10 text-primary border-primary/20',
    },
    {
      icon: 'ChatBubbleLeftRightIcon',
      titleAr: 'الدردشة المباشرة',
      titleEn: 'Live Chat',
      descriptionAr: 'دعم فوري عبر الإنترنت',
      descriptionEn: 'Instant online support',
      actionAr: 'بدء المحادثة',
      actionEn: 'Start Chat',
      link: 'https://wa.me/966590041741',
      color: 'bg-accent/10 text-accent border-accent/20',
    },
  ];

  if (!isHydrated) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-card rounded-lg p-6 border border-border animate-pulse">
            <div className="w-12 h-12 bg-muted rounded-full mb-4"></div>
            <div className="h-6 bg-muted rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-muted rounded w-full mb-4"></div>
            <div className="h-10 bg-muted rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
      {quickOptions.map((option, index) => (
        <a
          key={index}
          href={option.link}
          className="bg-card rounded-lg p-6 border border-border hover:shadow-lg transition-premium group"
        >
          <div
            className={`flex items-center justify-center w-14 h-14 rounded-full ${option.color} border mb-4 group-hover:scale-110 transition-premium`}
          >
            <Icon name={option.icon as any} size={28} />
          </div>

          <h3 className="text-lg font-bold text-foreground font-amiri mb-2">
            {t(option.titleAr, option.titleEn)}
          </h3>

          <p className="text-sm text-muted-foreground mb-4">
            {t(option.descriptionAr, option.descriptionEn)}
          </p>

          <div className="flex items-center gap-2 text-primary font-medium group-hover:gap-3 transition-premium">
            <span>{t(option.actionAr, option.actionEn)}</span>
            <Icon name="ArrowLeftIcon" size={16} />
          </div>
        </a>
      ))}
    </div>
  );
};

export default QuickContactOptions;